import { useId, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from '@ecommerce/nest-ssr/common';
import { ArticlesActions, ArticlesState } from '../../../../../../reducers/articles';
import WidgetArticleProps from 'modules/shared/widgets/components/widget-articles/widget-article.props';
import { ModifiedImgProps } from 'modules/shared/widgets/components/modified-img.props';
import { WidgetArticleInterface } from 'modules/shared/widgets/components/widget-articles/interfaces/widget-article.interface';

interface NativeSlot {
    id: string;
    index: number;
    relIndex: number;
    article?: WidgetArticleProps;
}

const SLOTS_IN_WIDGET = 3;

const toSponsoredArticle = (slot: Required<NativeSlot>): WidgetArticleInterface => ({
    ...slot.article,
    id: slot.id,
    modifiedImg: slot.article.img,
    isSponsored: true
});

export const useNativeArticles
= (articles: (WidgetArticleProps & ModifiedImgProps)[]): { mergedItems: WidgetArticleInterface[]; nativeSlots: NativeSlot[]; } => {
    const dispatch = useDispatch();
    const baseId = useId();

    const slotIds = useMemo(
        () => Array.from({ length: SLOTS_IN_WIDGET }, (unused, i) => `native-article-${baseId}-${i}`),
        [baseId]
    );

    const nativeArticlesState = useSelector((state: { articles: ArticlesState; }) => state.articles?.nativeArticles);

    const nativeSlots: NativeSlot[] = useMemo(() => {
        return slotIds.map((id, relIndex) => {
            const index = nativeArticlesState?.findIndex(item => item.id === id);

            const article = index >= 0 && nativeArticlesState[index].article;

            return { id, index, relIndex, article: article || undefined };
        });
    }, [slotIds, nativeArticlesState]);

    useEffect(() => {
        slotIds.forEach(id => {
            dispatch({
                type: ArticlesActions.AddNativeArticle,
                payload: { id, article: null }
            });
        });
    }, [dispatch, slotIds]);

    const mergedItems = useMemo(() => {
        const items: WidgetArticleInterface[] = Array.of(...articles);
        const lastArticlesIndex = articles.length - 1;
        const nonEmptySlots = nativeSlots.filter(item => item.article && item.relIndex >= 0) as Required<NativeSlot>[];
        const inRangeSlots = nonEmptySlots.filter(item => item.relIndex <= lastArticlesIndex);
        const outOfRangeSlots = nonEmptySlots.filter(item => item.relIndex > lastArticlesIndex);

        for (const slot of inRangeSlots) {
            items[slot.relIndex] = toSponsoredArticle(slot);
        }

        items.push(...outOfRangeSlots.map(slot => toSponsoredArticle(slot)));

        return items;
    }, [nativeSlots, articles]);

    return { mergedItems, nativeSlots };
};
